import { REVIEW_STATUS_LABEL } from "@/lib/i18nLabels";
import OverReceivedBadge from "@/components/OverReceivedBadge";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MessageSquare, Mail, Users as UsersIcon, MapPin, FileText, Search, X, Download, AlertTriangle, CheckCircle2, ClockAlert, Wallet, TrendingDown, Calendar, Filter, ChevronDown, Plus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { useRefresh } from "@/lib/useRefresh";
import { Button } from "@/components/ui/button";
import { fmtDate, formatIDR } from "@/lib/constants";
import CollectionFollowupDialog from "@/components/collection/CollectionFollowupDialog";
import CollectionAssignDialog from "@/components/collection/CollectionAssignDialog";
import CollectionBulkAssignDialog from "@/components/collection/CollectionBulkAssignDialog";
import BulkFillDueDateDialog from "@/components/finance-report/BulkFillDueDateDialog";
import LinkRetentionSourceDialog from "@/components/collection/LinkRetentionSourceDialog";
import ClaimPaymentDetail from "@/components/collection/ClaimPaymentDetail";
import StickyContextBar from "@/components/StickyContextBar";
import SyncStickyTableHead from "@/components/table/SyncStickyTableHead";
import { InvoiceIdentityCell, InvoiceIdentityPanel } from "@/components/collection/InvoiceIdentityCell";
import CompleteIdentityDialog from "@/components/collection/CompleteIdentityDialog";
import IncompleteInvoicesTab from "@/components/collection/IncompleteInvoicesTab";
import DraftVerificationQueue from "@/components/collection/DraftVerificationQueue";
import RetentionClaimQueue from "@/components/collection/RetentionClaimQueue";

const AGING_COLOR = {
  current: "bg-emerald-50 text-emerald-700 border-emerald-300",
  "0_30": "bg-sky-50 text-sky-700 border-sky-300",
  "31_60": "bg-amber-50 text-amber-700 border-amber-300",
  "61_90": "bg-orange-50 text-orange-700 border-orange-300",
  over_90: "bg-rose-50 text-rose-700 border-rose-300",
};
const STATUS_COLOR = {
  paid: "bg-emerald-100 text-emerald-800",
  open: "bg-sky-100 text-sky-800",
  overdue: "bg-rose-100 text-rose-800",
  needs_verification: "bg-amber-100 text-amber-800",
  needs_classification: "bg-orange-100 text-orange-800",
  incomplete: "bg-slate-200 text-slate-600",
};

// 2026-09-26 (user request): status tindak lanjut piutang (derived backend).
const FU_STATUS = {
  belum: { label: "Belum Ditindaklanjuti", cls: "bg-slate-100 text-slate-500 border-slate-300" },
  menunggu_janji: { label: "Menunggu Janji Bayar", cls: "bg-sky-50 text-sky-700 border-sky-300" },
  janji_lewat: { label: "Janji Bayar Terlewat", cls: "bg-rose-50 text-rose-700 border-rose-300" },
  terjadwal: { label: "Follow-up Terjadwal", cls: "bg-indigo-50 text-indigo-700 border-indigo-300" },
  perlu_followup: { label: "Perlu Follow-up", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  dalam_proses: { label: "Dalam Tindak Lanjut", cls: "bg-blue-50 text-blue-700 border-blue-300" },
  eskalasi: { label: "Eskalasi", cls: "bg-orange-100 text-orange-800 border-orange-300" },
  selesai: { label: "Selesai", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
};

// 149-cq (PROMPT 16-R2A): helper konsisten untuk deteksi invoice lunas.
// Dipakai untuk menyembunyikan angka aging overdue ("+408 hari") saat invoice
// sudah dibayar penuh — tanpa mengubah nilai invoice, penerimaan, retensi,
// due date, atau histori pembayaran.
function isPaidRow(r) {
  if (!r) return false;
  return r.status === "paid" || (Number(r.outstanding) || 0) <= 0;
}

// Status asal kanonis backend (origin_status) — TIDAK dihitung di frontend.
const ORIGIN_TONE = { official: "bg-sky-50 text-sky-800 border-sky-300", legacy: "bg-slate-100 text-slate-700 border-slate-300", draft: "bg-amber-50 text-amber-800 border-amber-300", unknown: "bg-slate-50 text-slate-500 border-slate-300" };
const STATUS_TONE = { official_paid: "bg-emerald-100 text-emerald-800", official_partially_paid: "bg-orange-100 text-orange-800", official_verified_unpaid: "bg-rose-100 text-rose-800" };
const OriginStatusBadges = ({ r }) => {
  const s = r.origin_status;
  if (!s) return null;
  return (
    <div className="flex flex-col items-center gap-0.5" title={s.status_explanation} data-testid={`origin-status-${r.entry_id}`}>
      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${ORIGIN_TONE[s.invoice_origin]}`} data-testid={`origin-badge-${r.entry_id}`}>{s.origin_label}</span>
      <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold ${STATUS_TONE[s.status_code] || "bg-slate-100 text-slate-700"}`} data-testid={`status-payment-${r.entry_id}`}>{s.status_label}</span>
      {s.reconciliation_required && <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase border bg-amber-50 text-amber-800 border-amber-300" data-testid={`status-recon-${r.entry_id}`}>Perlu Rekonsiliasi</span>}
      {s.classification_status === "needs_classification" || s.classification_status === "partially_classified" ? <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase border bg-amber-50 text-amber-800 border-amber-300">Perlu Klasifikasi</span> : null}
    </div>
  );
};

export default function CollectionPage() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  // 149-cn (PROMPT 16-P0): permission enforcement — ONLY `collection.*`. Hapus
  // OR-fallback ke `projects` supaya Active=OFF pada Collection benar-benar
  // menolak Project LV3 walau punya izin projects.view.
  const canView = canPerm(user, "collection", "view");
  const canCreate = canPerm(user, "collection", "create");
  const canAssign = canPerm(user, "collection", "delete");
  const canExport = canView;
  // 2026-10-20 (user request "IDENTITAS INVOICE"): perm granular untuk melengkapi
  // identitas invoice — nomor, referensi, tanggal, sumber, PIC.
  const canCompleteIdentity = canPerm(user, "collection", "complete_identity");
  const [completeDlg, setCompleteDlg] = useState({ open: false, row: null });
  // 2026-10-17 (user request): tombol Isi Due Date bulk edit — perm granular
  // `finance_report.fill_due_date` (diset admin via Access Control).
  const canFillDueDate = canPerm(user, "finance_report", "fill_due_date");
  const [bulkFillOpen, setBulkFillOpen] = useState(false);

  const [rows, setRows] = useState([]);

  // 16-STATUS-KONSISTEN-R1 (2026-06-14, user request): badge klasifikasi di
  // tabel langsung membuka dialog (reuse OverReceivedBadge) — setelah simpan,
  // baris diperbarui in-place tanpa refetch penuh.
  const onOverClassified = (pid, doc) => setRows((prev) => prev.map((row) => (
    row.project_id === pid ? { ...row, classification_status: "classified", over_classification: doc } : row
  )));
  const [meta, setMeta] = useState({ aging_buckets: [], comm_media: [], result_kinds: [] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  // 2026-10-19 (R223 closeout "FIX FILTER KLAIM"): daftar status yang sah.
  const ALLOWED_STATUS = ["payment_lunas", "payment_sebagian", "payment_belum", "overdue", "no_due_date", "paid", "open", "needs_classification", "needs_verification", "outstanding_all", "retention_claim",
    "claim_remaining_only", "claim_belum_dibayar", "claim_dibayar_sebagian", "claim_lunas", "claim_lewat_jt", "claim_no_due", "claim_perlu_verifikasi",
    "claim_tertaut_valid", "claim_fallback", "claim_belum_tertaut", "claim_invalid", "claim_overclaim"];
  // 2026-10-19 (R223 closeout): filter diinisialisasi SINKRON dari URL
  // (direct URL & refresh menghasilkan dataset yang sama — filter ikut pada
  // request PERTAMA, bukan diterapkan setelah data "semua" termuat dulu).
  const [filters, setFilters] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    const st = sp.get("status");
    const isClaim = st === "retention_claim" || String(st || "").startsWith("claim_");
    return {
      status: ALLOWED_STATUS.includes(st) ? st : "all",
      aging: "all",
      retention_only: sp.get("retention") === "true" && !isClaim,
      q: "",
      no_pic: false,
      no_followup: false,
      project_id: sp.get("project_id") || null,
    };
  });
  const [followupDlg, setFollowupDlg] = useState({ open: false, row: null });
  const [assignDlg, setAssignDlg] = useState({ open: false, row: null });
  // 2026-09-26 (user request): bulk assign PIC — seleksi baris + dialog konfirmasi.
  const [selected, setSelected] = useState({});
  const [bulkDlg, setBulkDlg] = useState(false);
  // 2026-10-19 (user request anti double-counting): dialog FAT menautkan klaim
  // retensi legacy ke invoice sumber.
  const [linkSourceClaim, setLinkSourceClaim] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [history, setHistory] = useState([]);
  // 2026-10-19 (R223 closeout): anti race-condition — hanya respons request
  // TERAKHIR yang boleh mengisi tabel. Tanpa ini, request "Semua" yang lebih
  // berat bisa resolve setelah request filter klaim dan menimpa daftar
  // (akar bug: label filter klaim tapi tabel menampilkan 142 transaksi umum).
  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setRows([]);  // kosongkan daftar lama selama memuat — pengguna tidak
                  // melihat dataset filter sebelumnya.
    try {
      const params = new URLSearchParams();
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.aging !== "all") params.set("aging", filters.aging);
      if (filters.retention_only) params.set("retention_only", "true");
      if (filters.no_pic) params.set("no_pic", "true");
      if (filters.no_followup) params.set("no_followup", "true");
      if (filters.q) params.set("q", filters.q);
      if (filters.project_id) params.set("project_id", filters.project_id);
      const [inv, sum] = await Promise.all([
        api.get(`/collection/invoices?${params.toString()}`),
        api.get("/collection/summary"),
      ]);
      if (seq !== reqSeq.current) return;  // respons basi — buang
      setRows(inv.data.items || []);
      setMeta({
        aging_buckets: inv.data.aging_buckets,
        comm_media: inv.data.comm_media,
        result_kinds: inv.data.result_kinds,
      });
      setSummary(sum.data);
    } catch (e) { if (seq === reqSeq.current) toast.error("Gagal memuat data collection"); }
    finally { if (seq === reqSeq.current) setLoading(false); }
  }, [filters]);

  useEffect(() => { if (canView) load(); }, [canView, load]);
  useRefresh(load);

  const openDetail = async (r) => {
    setDetailRow(r);
    try {
      const { data } = await api.get(`/collection/followups?project_id=${r.project_id}&entry_id=${r.entry_id}`);
      setHistory(data.items || []);
    } catch { setHistory([]); }
  };

  // 2026-10-17 (user request): deep-link dari widget Dasbor (Top 5 Terlambat) —
  // /collection?focus=<entry_key> langsung membuka rincian invoice asal.
  const [searchParams, setSearchParams] = useSearchParams();

  // 2026-10-20 (user request D): tab aktif disinkronkan ke URL (?tab=incomplete)
  // agar bookmark/refresh mempertahankan tab.
  const urlTab = searchParams.get("tab");
  const tab = urlTab === "incomplete" ? "incomplete" : "daftar";
  const setTab = (next) => {
    const n = new URLSearchParams(searchParams);
    if (next === "incomplete") n.set("tab", "incomplete");
    else n.delete("tab");
    setSearchParams(n, { replace: true });
  };
  const [incSub, setIncSub] = useState(["identity", "retention"].includes(searchParams.get("sub")) ? searchParams.get("sub") : "draft");

  // 2026-10-19 (R223 closeout): SATU fungsi filter yang sama untuk klik kartu
  // Dasbor, tombol di Collection, dan pilihan dropdown status. Filter klaim
  // otomatis mematikan "Hanya Retensi" (invoice sumber) yang tidak kompatibel.
  const applyStatusFilter = useCallback((st) => {
    const isClaimFilter = st === "retention_claim" || String(st).startsWith("claim_");
    setFilters((f) => ({ ...f, status: st, retention_only: isClaimFilter ? false : f.retention_only }));
  }, []);

  // 2026-10-19 (R223 closeout): terapkan param URL yang BERUBAH (klik kartu
  // Dasbor saat sudah di halaman Collection). Mount pertama sudah ditangani
  // initializer filters — efek ini hanya untuk navigasi berikutnya.
  useEffect(() => {
    const st = searchParams.get("status");
    if (st && ALLOWED_STATUS.includes(st) && filters.status !== st) {
      applyStatusFilter(st);
      return;  // sync effect di bawah menulis ulang URL
    }
    const ret = searchParams.get("retention") === "true";
    if (ret !== filters.retention_only && !String(filters.status).startsWith("claim_") && filters.status !== "retention_claim") {
      setFilters((f) => ({ ...f, retention_only: ret }));
    }
    const pid = searchParams.get("project_id") || null;
    if (pid !== filters.project_id) setFilters((f) => ({ ...f, project_id: pid }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // 2026-10-19 (R223 closeout): URL mengikuti state filter aktif — refresh &
  // direct URL menghasilkan dataset yang sama; "Hapus Filter" membersihkan
  // state + query param sekaligus (state all → params kosong).
  useEffect(() => {
    const next = new URLSearchParams();
    if (filters.status !== "all") next.set("status", filters.status);
    if (filters.retention_only) next.set("retention", "true");
    if (filters.project_id) next.set("project_id", filters.project_id);
    // 2026-10-21 (user request D): preserve `tab` dan `focus` saat sync filter.
    const keepTab = searchParams.get("tab");
    if (keepTab) next.set("tab", keepTab);
    const keepFocus = searchParams.get("focus");
    if (keepFocus) next.set("focus", keepFocus);
    const cur = window.location.search.replace(/^\?/, "");
    if (cur !== next.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.retention_only, filters.project_id]);
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (!focus || loading) return;
    const row = rows.find((r) => r.entry_key === focus);
    if (row) {
      openDetail(row);
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, loading]);

  const doExport = async () => {
    try {
      const res = await api.get("/collection/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `collection_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export siap");
    } catch (e) { toast.error("Gagal export"); }
  };

  const agingChart = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.aging_distribution || {}).map(([k, v]) => ({
      key: k, label: summary.aging_labels[k] || k, value: v,
    }));
  }, [summary]);
  const totalAging = agingChart.reduce((s, r) => s + r.value, 0);
  // 2026-10-19 closeout R221: flag tabel khusus klaim retensi — header & sel
  // 14 kolom khusus (spec user poin 3), jangan campur dgn invoice reguler.
  const isClaimView = filters.status === "retention_claim" || String(filters.status).startsWith("claim_");

  if (!canView) {
    return <div className="p-8 text-center text-slate-500" data-testid="collection-no-access">Anda tidak memiliki akses Collection Monitoring.</div>;
  }

  return (
    <div className="p-6 space-y-4" data-testid="collection-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1D3A]">Collection & Retention Monitoring</h1>
          <p className="text-xs text-slate-500 mt-1">Pemantauan AR + retensi. Seluruh nilai finansial dihitung oleh <span className="font-semibold text-emerald-700">satu engine A.R. yang sama</span> untuk Dasbor, Collection, dan Monitoring Proyek. Sistem ini disiapkan bertahap menjadi sumber laporan keuangan resmi pengganti software akuntansi yang digunakan saat ini.</p>
        </div>
        {canExport && (
          <Button data-testid="btn-export-collection" onClick={doExport} variant="outline" className="rounded-none text-xs border-slate-300">
            <Download size={12} className="mr-1" /> Ekspor CSV
          </Button>
        )}
      </div>

      {/* Tabs: Daftar Invoice vs Antrean Kelengkapan */}
      <div className="flex gap-1 border-b border-slate-200" data-testid="collection-tabs">
        <button type="button" data-testid="tab-daftar"
          onClick={() => setTab("daftar")}
          className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors ${tab === "daftar" ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Daftar Invoice
        </button>
        <button type="button" data-testid="tab-incomplete"
          onClick={() => setTab("incomplete")}
          className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors ${tab === "incomplete" ? "border-[#FF5A1F] text-[#0B1D3A]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          Invoice Perlu Dilengkapi
        </button>
      </div>

      {tab === "incomplete" && (
        <div className="flex gap-1" data-testid="incomplete-subtabs">
          {[["draft", "Invoice Draft / Perlu Verifikasi"], ["identity", "Identitas Invoice"], ["retention", "Klaim Retensi Perlu Verifikasi"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setIncSub(k)} data-testid={`incomplete-sub-${k}`}
              className={`px-3 py-1 text-[11px] border ${incSub === k ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300"}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "incomplete" && incSub === "draft" && <DraftVerificationQueue onChanged={() => load()} initialCard={searchParams.get("card") || ""}
        onReset={() => { if (searchParams.get("card")) { const n = new URLSearchParams(searchParams); n.delete("card"); setSearchParams(n, { replace: true }); } }} />}
      {tab === "incomplete" && incSub === "retention" && <RetentionClaimQueue onChanged={() => load()} initialProject={searchParams.get("project") || ""} />}
      {tab === "incomplete" && incSub === "identity" && (
        <IncompleteInvoicesTab
          canComplete={canCompleteIdentity}
          onRowChanged={(nr) => setRows((prev) => prev.map((x) => (x.entry_key === nr.entry_key ? nr : x)))} />
      )}

      {tab === "daftar" && (<>

      <StickyContextBar
        segments={["Collection & Retention", `${rows.length} invoice`]}
        extra={<span className="text-slate-400 normal-case">{filters.status === "all" ? "Semua Status" : filters.status === "paid" ? "Lunas" : filters.status === "overdue" ? "Terlambat" : "Terbuka"}</span>}
      />

      {/* Summary cards */}
      {summary && (
        <>
          {summary.snapshot_id && (
            <div className="px-3 py-1.5 bg-slate-50 border border-slate-200 text-[10px] text-slate-500" data-testid="collection-snapshot-info">
              Snapshot per <b>{fmtDate(summary.as_of)}</b> · {summary.overdue_count} invoice overdue · total <b>{formatIDR(summary.total_overdue)}</b>
              {summary.no_due_date_count > 0 && (
                <> · <button type="button" data-testid="link-no-due-date"
                  className="underline hover:text-[#FF5A1F]"
                  onClick={() => setFilters({ ...filters, status: "no_due_date" })}>
                  {summary.no_due_date_count} invoice tanpa due date
                </button>
                {canFillDueDate && (
                  <> · <button type="button" data-testid="btn-fill-due-date-collection"
                    onClick={() => setBulkFillOpen(true)}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold uppercase tracking-wider">
                    <Calendar size={9} /> Isi Due Date
                  </button></>
                )}
                </>
              )}
              {summary.engine && <> · Sumber: {summary.engine}</>}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="collection-summary">
            {/* 2026-10-18 (user request): kartu ringkasan CLICKABLE — klik
                membuka daftar invoice pembentuk angkanya; jumlah & total daftar
                dijamin sama dengan kartu (predikat backend identik). */}
            <SumCard icon={<Wallet size={16} />} label="Total Piutang" value={formatIDR(summary.total_outstanding)} tone="sky"
              sub={`${summary.open_count ?? 0} transaksi terbuka`} testid="card-total-piutang"
              active={filters.status === "outstanding_all" && !filters.retention_only}
              onClick={() => setFilters({ ...filters, status: "outstanding_all", retention_only: false })} />
            <SumCard icon={<ClockAlert size={16} />} label="Total Terlambat" value={formatIDR(summary.total_overdue)} tone="rose"
              sub={`${summary.overdue_count} invoice`} testid="card-total-terlambat"
              active={filters.status === "overdue" && !filters.retention_only}
              onClick={() => setFilters({ ...filters, status: "overdue", retention_only: false })} />
            <SumCard icon={<TrendingDown size={16} />} label="Retensi Aktif" value={formatIDR(summary.retention_active)} tone="orange"
              sub="masih ditahan, belum diklaim" testid="card-retensi-aktif"
              active={!!filters.retention_only}
              onClick={() => setFilters({ ...filters, status: "all", retention_only: true })} />
            <SumCard icon={<Calendar size={16} />} label="Janji Bayar 7 Hari" value={`${summary.upcoming_promises.length}`} tone="emerald" testid="card-janji-bayar" />
            {/* 2026-10-21 (user request "SINKRONKAN COUNTER COLLECTION"):
                counter status pembayaran — SEMUA entri Collection (bukan hanya
                invoice resmi terverifikasi). Invoice boleh sekaligus Lunas +
                Perlu Verifikasi; counter Perlu Verifikasi tetap terpisah. */}
            {[
              ["official_verified_unpaid", "Belum Dibayar — Invoice Resmi", "rose", "outstanding_total", ClockAlert],
              ["official_partially_paid", "Dibayar Sebagian — Invoice Resmi", "orange", "outstanding_total", TrendingDown],
              ["official_paid", "Lunas — Invoice Resmi", "emerald", "paid_total", CheckCircle2],
              ["legacy_settled", "Legacy tercatat lunas", "slate", "paid_total", CheckCircle2],
              ["official_legacy_settled", "Invoice Resmi · Legacy tercatat lunas", "slate", "paid_total", CheckCircle2],
              ["draft", "Draft — Perlu Verifikasi", "amber", "outstanding_total", ClockAlert],
              ["unknown", "Asal data belum dapat dipastikan", "slate", "outstanding_total", ClockAlert],
            ].map(([code, label, tone, fld, Ic]) => {
              const b = summary.origin_status_counts?.[code] || { count: 0 };
              return (
                <SumCard key={code} icon={<Ic size={16} />} label={label} value={`${b.count}`} tone={tone}
                  sub={`${fld === "paid_total" ? "diterima" : "sisa"} ${formatIDR(b[fld] || 0)}${b.reconciliation_count ? ` · ${b.reconciliation_count} perlu rekonsiliasi` : ""}`} testid={`card-origin-${code}`}
                  active={filters.status === `origin_${code}` && !filters.retention_only}
                  onClick={() => setFilters({ ...filters, status: `origin_${code}`, retention_only: false })}
                  tooltip="Status kanonis per entri (selain klaim retensi): asal data Invoice Resmi / Legacy / Draft / Belum Dipastikan. Legacy tercatat lunas berasal dari saldo historis dan bukan pelunasan invoice resmi." />
              );
            })}
          </div>
          {/* 2026-10-18: strip klasifikasi retensi 5 sub-status (engine A.R.)
              — klasifikasi sama dgn Dasbor, Monitoring & Retensi/Collection. */}
          {summary.retention_breakdown && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="retention-breakdown">
              {[
                { k: "held_total", label: "Retensi Masih Ditahan", cls: "text-sky-700 bg-sky-50 border-sky-200" },
                { k: "not_claimed_total", label: "Retensi Belum Diklaim", cls: "text-orange-700 bg-orange-50 border-orange-200" },
                { k: "claimed_waiting", label: "Klaim Menunggu Pembayaran", cls: "text-amber-700 bg-amber-50 border-amber-200" },
                { k: "claimed_partial", label: "Klaim Dibayar Sebagian", cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
                { k: "claimed_paid", label: "Retensi Sudah Dicairkan", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
                {
                  k: "needs_verification", label: "Perlu Verifikasi (Kualitas Data)",
                  cls: "text-rose-700 bg-rose-50 border-rose-300",
                  // 2026-10-19 closeout: triple terpisah (bukan satu nominal).
                  getter: (bd) => bd.needs_verification?.total ?? 0,
                  countGetter: (bd) => bd.needs_verification?.count ?? 0,
                  tripleGetter: () => summary.retention_claim_verification_detail || null,
                },
              ].map((it) => (
                <div key={it.k} className={`border px-2.5 py-1.5 ${it.cls}`} data-testid={`retention-sub-${it.k}`}>
                  <div className="text-[9px] uppercase tracking-wider font-semibold opacity-80">{it.label}</div>
                  {it.tripleGetter && it.tripleGetter() ? (
                    <div className="mt-0.5 space-y-0.5 tabular-nums leading-tight" data-testid="retention-verif-triple">
                      <div className="text-xs"><b>{it.tripleGetter().count}</b> <span className="opacity-80">klaim</span></div>
                      <div className="text-[10px]"><span className="opacity-70">Nilai neto:</span> <b>{formatIDR(it.tripleGetter().net_total)}</b></div>
                      <div className="text-[10px]"><span className="opacity-70">Sisa belum diterima:</span> <b>{formatIDR(it.tripleGetter().remaining_total)}</b></div>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-bold tabular-nums">{formatIDR(it.getter ? it.getter(summary.retention_breakdown) : (summary.retention_breakdown[it.k] || 0))}</div>
                      {it.countGetter && it.countGetter(summary.retention_breakdown) > 0 && (
                        <div className="text-[9px] opacity-80 font-semibold">{it.countGetter(summary.retention_breakdown)} klaim · pilih invoice sumber</div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* 2026-10-19 closeout R221: breakdown "Retensi Belum Diklaim" yang
              dapat dihitung ulang — setiap kategori clickable ke daftar klaim
              pembentuknya. Rumus: Belum Diklaim = Masih Ditahan (net) −
              Tertaut Eksplisit − Fallback Proyek. */}
          {summary.retention_components && (
            <div className="border border-slate-300 bg-white" data-testid="retention-components">
              <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Pembentuk Retensi Belum Diklaim</span>
                {summary.retention_components.sementara_ada_perlu_verifikasi && (
                  <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-rose-600 text-white" data-testid="badge-sementara">
                    SEMENTARA — ADA KLAIM PERLU VERIFIKASI
                  </span>
                )}
              </div>
              {(() => {
                const rc = summary.retention_components;
                const cells = [
                  { key: "held", label: "Retensi Masih Ditahan", val: rc.held_net, note: "setelah rilis, sebelum klaim", filter: null, tone: "text-sky-700" },
                  { key: "tertaut", label: "Klaim Tertaut Eksplisit", val: rc.linked_consumed_total, count: rc.tertaut_valid?.count ?? 0, filter: "claim_tertaut_valid", sign: "−", tone: "text-emerald-700" },
                  { key: "fallback", label: "Klaim Terpetakan Fallback Proyek", val: rc.fallback?.recognized_total ?? 0, count: rc.fallback?.count ?? 0, filter: "claim_fallback", sign: "−", tone: "text-amber-700", note: "aturan fallback kelompok proyek" },
                  { key: "belum_tertaut", label: "Klaim Belum Tertaut", val: rc.belum_tertaut?.total ?? 0, count: rc.belum_tertaut?.count ?? 0, filter: "claim_belum_tertaut", tone: "text-rose-700", note: "tidak mengurangi" },
                  { key: "invalid", label: "Klaim Invalid", val: rc.invalid?.total ?? 0, count: rc.invalid?.count ?? 0, filter: "claim_invalid", tone: "text-rose-700", note: "tidak mengurangi" },
                  { key: "overclaim", label: "Over-Claim", val: rc.overclaim?.total ?? 0, count: rc.overclaim?.count ?? 0, filter: "claim_overclaim", tone: "text-rose-700", note: "tidak mengurangi" },
                ];
                return (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-px bg-slate-100">
                      {cells.map((c) => (
                        <button key={c.key} type="button" data-testid={`rc-${c.key}`}
                          disabled={!c.filter}
                          onClick={() => c.filter && applyStatusFilter(c.filter)}
                          title={c.filter ? `${c.count} klaim — klik untuk membuka daftar pembentuknya` : c.note}
                          className={`bg-white px-3 py-2 text-left ${c.filter ? "hover:bg-slate-50 cursor-pointer" : "cursor-default"}`}>
                          <div className="text-[9px] uppercase tracking-wider font-semibold text-slate-500 leading-tight">{c.label}</div>
                          <div className={`text-sm font-bold tabular-nums ${c.tone}`}>
                            {c.sign || ""}{formatIDR(c.val ?? 0)}
                          </div>
                          {c.count != null && (
                            <div className="text-[9px] text-slate-500">{c.count} klaim{c.note ? ` · ${c.note}` : ""}</div>
                          )}
                          {!c.count && c.note && <div className="text-[9px] text-slate-400">{c.note}</div>}
                        </button>
                      ))}
                    </div>
                    <div className="px-3 py-2 border-t border-slate-200 flex items-center gap-2 flex-wrap text-[11px] tabular-nums" data-testid="rc-formula">
                      <span className="text-slate-600">Total pengurang diakui: <b className="text-emerald-700">{formatIDR(rc.total_pengurang_diakui ?? 0)}</b></span>
                      <span className="text-slate-400">→</span>
                      <span className="text-slate-800">
                        Hasil Retensi Belum Diklaim: <b data-testid="rc-hasil">{formatIDR(rc.held_net ?? 0)}</b> − <b>{formatIDR(rc.total_pengurang_diakui ?? 0)}</b> ={" "}
                        <b className="text-orange-700" data-testid="rc-hasil-final">{formatIDR(rc.not_claimed_total_final ?? 0)}</b>
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          {summary.pending_verification?.count > 0 && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-[11px] text-amber-900" data-testid="pending-verif-strip">
              Perlu Verifikasi (draft — <b>belum masuk piutang resmi</b>):{" "}
              <button type="button" className="underline font-semibold hover:text-amber-700" data-testid="link-pending-verif"
                onClick={() => setFilters({ ...filters, status: "needs_verification", retention_only: false })}>
                {summary.pending_verification.count} invoice
              </button>{" "}· total {formatIDR(summary.pending_verification.total)}. Angka ini tidak dihitung dalam Total Piutang maupun aging sampai diverifikasi.
            </div>
          )}
        </>
      )}

      {/* 2026-10-18 (user request): banner filter aktif — judul daftar
          menunjukkan filter yang dipakai + jumlah transaksi & total rupiah yang
          sama dengan kartu + tombol Hapus Filter/Kembali ke Semua. */}
      {(filters.status !== "all" || filters.retention_only || filters.project_id) && (() => {
        const LABELS = {
          outstanding_all: "Total Piutang — invoice terbuka + klaim retensi belum dibayar",
          overdue: "Total Terlambat", no_due_date: "Jatuh Tempo Belum Diisi",
          paid: "Lunas", open: "Belum Jatuh Tempo",
          payment_belum: "Belum Dibayar — Semua Entri",
          payment_sebagian: "Dibayar Sebagian — Semua Entri",
          payment_lunas: "Lunas — Semua Entri",
          origin_official_verified_unpaid: "Belum Dibayar — Invoice Resmi", origin_official_partially_paid: "Dibayar Sebagian — Invoice Resmi",
          origin_official_paid: "Lunas — Invoice Resmi", origin_legacy_settled: "Legacy tercatat lunas",
          origin_official_legacy_settled: "Invoice Resmi · Legacy tercatat lunas", origin_draft: "Draft — Perlu Verifikasi", origin_unknown: "Asal data belum dapat dipastikan",
          needs_verification: "Perlu Verifikasi (draft — bukan piutang resmi)",
          needs_classification: "Perlu Klasifikasi", classified: "Sudah Diklasifikasi",
          retention_claim: "Klaim Retensi (Semua)",
          claim_remaining_only: "Klaim Retensi · Sisa > 0 (Pembentuk Total Sisa Klaim)",
          claim_belum_dibayar: "Klaim Retensi · Belum Dibayar",
          claim_dibayar_sebagian: "Klaim Retensi · Dibayar Sebagian",
          claim_lunas: "Klaim Retensi · Lunas",
          claim_lewat_jt: "Klaim Retensi · Lewat Jatuh Tempo",
          claim_no_due: "Klaim Retensi · Jatuh Tempo Belum Diisi",
          claim_perlu_verifikasi: "Klaim Retensi · Perlu Verifikasi",
          claim_tertaut_valid: "Klaim Retensi · Tertaut Eksplisit",
          claim_fallback: "Klaim Retensi · Terpetakan Fallback Proyek",
          claim_belum_tertaut: "Klaim Retensi · Belum Tertaut",
          claim_invalid: "Klaim Retensi · Invalid",
          claim_overclaim: "Klaim Retensi · Over-Claim",
        };
        const projLabel = filters.project_id ? (rows[0]?.project_name || "proyek terpilih") : null;
        const baseLabel = filters.retention_only ? "Retensi Aktif — masih ditahan, belum diklaim" : (LABELS[filters.status] || "Semua status");
        const label = baseLabel + (projLabel ? ` · Proyek: ${projLabel} (termasuk WO/VO anak)` : "");
        const total = filters.retention_only
          ? rows.reduce((s, r) => s + Math.round(Number(r.retention_outstanding) || 0), 0)
          : filters.status === "paid"
            ? rows.reduce((s, r) => s + Math.round(Number(r.paid_amount) || 0), 0)
            : rows.reduce((s, r) => s + Math.round(Number(r.outstanding) || 0), 0);
        // 2026-10-19 closeout R221: header hasil klaim menampilkan sisa DAN
        // nilai neto secara terpisah (anti satu nominal ambigu).
        const netoTotal = isClaimView
          ? rows.reduce((s, r) => s + Math.round(Number(r.cash_due ?? r.claim_gross_amount ?? r.gross_invoice_amount) || 0), 0)
          : 0;
        return (
          <div className="px-3 py-2 bg-orange-50 border border-orange-200 flex items-center gap-2 flex-wrap" data-testid="active-filter-banner">
            <span className="text-[11px] text-orange-900">
              Daftar menampilkan: <b>{label}</b> · <b data-testid="active-filter-count">{rows.length} transaksi</b>
              {isClaimView ? (
                <> · sisa belum diterima <b data-testid="active-filter-total">{formatIDR(total)}</b> · nilai neto <b data-testid="active-filter-neto">{formatIDR(netoTotal)}</b></>
              ) : (
                <> · total <b data-testid="active-filter-total">{formatIDR(total)}</b></>
              )}
            </span>
            <button type="button" data-testid="btn-clear-filter"
              onClick={() => {
                // 2026-10-19 closeout: hapus TOTAL — status, aging, retention_only,
                // query (q), PIC/followup, project_id, DAN sisa query param URL.
                setFilters({ status: "all", aging: "all", retention_only: false, q: "", no_pic: false, no_followup: false, project_id: null });
                setSearchParams(new URLSearchParams(), { replace: true });
              }}
              className="ml-auto px-2.5 py-1 bg-white border border-orange-300 text-orange-800 text-[10px] uppercase tracking-wider font-semibold hover:bg-orange-100">
              Hapus Filter / Kembali ke Semua
            </button>
          </div>
        );
      })()}

      {/* Aging distribution */}
      {summary && totalAging > 0 && (
        <div className="bg-white border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Distribusi Aging Outstanding</div>
          <div className="flex h-6 border border-slate-200 overflow-hidden" data-testid="aging-bar">
            {agingChart.map((s) => s.value > 0 && (
              <div key={s.key} style={{ width: `${(s.value / totalAging * 100).toFixed(1)}%` }}
                title={`${s.label}: ${formatIDR(s.value)}`}
                className={`${s.key === "current" ? "bg-emerald-500" : s.key === "0_30" ? "bg-sky-500" : s.key === "31_60" ? "bg-amber-500" : s.key === "61_90" ? "bg-orange-500" : "bg-rose-500"} text-white text-[10px] flex items-center justify-center overflow-hidden`}>
                {(s.value / totalAging * 100).toFixed(0)}%
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-2 text-[10px]">
            {agingChart.map((s) => (
              <span key={s.key} className="tabular-nums">
                <span className={`inline-block w-2 h-2 mr-1 ${s.key === "current" ? "bg-emerald-500" : s.key === "0_30" ? "bg-sky-500" : s.key === "31_60" ? "bg-amber-500" : s.key === "61_90" ? "bg-orange-500" : "bg-rose-500"}`} />
                {s.label}: {formatIDR(s.value)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming promises */}
      {summary?.upcoming_promises?.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 p-3" data-testid="upcoming-promises">
          <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold mb-2">Janji Pembayaran Terdekat</div>
          <div className="space-y-1">
            {summary.upcoming_promises.slice(0, 5).map((pr) => (
              <div key={`${pr.promise_date}-${pr.client}-${pr.project_name}`} className="flex items-center justify-between text-xs">
                <span className="text-slate-800">{fmtDate(pr.promise_date)} — {pr.client} · {pr.project_name}</span>
                <span className="font-semibold tabular-nums text-emerald-700">{pr.promise_amount ? formatIDR(pr.promise_amount) : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" data-testid="input-collection-search"
            placeholder="Cari client, project, invoice#, PIC..." value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="pl-7 pr-3 py-1.5 border border-slate-300 text-xs w-72 focus:outline-none focus:border-[#FF5A1F]" />
        </div>
        <select data-testid="filter-status" value={filters.status} onChange={(e) => applyStatusFilter(e.target.value)}
          className="border border-slate-300 py-1.5 px-2 text-xs">
          <option value="all">Semua</option>
          <option value="outstanding_all">Terbuka (Semua Piutang)</option>
          <option value="open">Belum Jatuh Tempo</option>
          <option value="overdue">Terlambat</option>
          <option value="no_due_date">Jatuh Tempo Belum Diisi</option>
          <option value="paid">Lunas</option>
          <option value="payment_belum">Belum Dibayar — Semua Entri</option>
          <option value="payment_sebagian">Dibayar Sebagian — Semua Entri</option>
          <option value="payment_lunas">Lunas — Semua Entri (angka saja)</option>
          <option value="origin_official_verified_unpaid">Belum Dibayar — Invoice Resmi</option>
          <option value="origin_official_partially_paid">Dibayar Sebagian — Invoice Resmi</option>
          <option value="origin_official_paid">Lunas — Invoice Resmi</option>
          <option value="origin_legacy_settled">Legacy tercatat lunas</option>
          <option value="origin_official_legacy_settled">Invoice Resmi · Legacy tercatat lunas</option>
          <option value="origin_draft">Draft — Perlu Verifikasi</option>
          <option value="origin_unknown">Asal data belum dapat dipastikan</option>
          <option value="retention_claim">Klaim Retensi</option>
          <option value="claim_remaining_only">Klaim: Sisa &gt; 0 (Pembentuk Total)</option>
          <option value="claim_belum_dibayar">Klaim: Belum Dibayar</option>
          <option value="claim_dibayar_sebagian">Klaim: Dibayar Sebagian</option>
          <option value="claim_lunas">Klaim: Lunas</option>
          <option value="claim_lewat_jt">Klaim: Lewat Jatuh Tempo</option>
          <option value="claim_no_due">Klaim: Jatuh Tempo Belum Diisi</option>
          <option value="claim_perlu_verifikasi">Klaim: Perlu Verifikasi</option>
          <option value="claim_tertaut_valid">Klaim: Tertaut Eksplisit</option>
          <option value="claim_fallback">Klaim: Terpetakan Fallback Proyek</option>
          <option value="claim_belum_tertaut">Klaim: Belum Tertaut</option>
          <option value="claim_invalid">Klaim: Invalid</option>
          <option value="claim_overclaim">Klaim: Over-Claim</option>
          <option value="needs_verification">Perlu Verifikasi</option>
          <option value="needs_classification">Perlu Klasifikasi</option>
          <option value="classified">Sudah Diklasifikasi</option>
        </select>
        <select data-testid="filter-aging" value={filters.aging} onChange={(e) => setFilters({ ...filters, aging: e.target.value })}
          className="border border-slate-300 py-1.5 px-2 text-xs">
          <option value="all">Semua Aging</option>
          {meta.aging_buckets.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" data-testid="filter-retention" checked={filters.retention_only}
            onChange={(e) => {
              // 2026-10-19 closeout: "Hanya Retensi" (invoice sumber) konflik
              // dengan sub-filter status klaim → reset status ke "all" supaya
              // kedua filter tidak saling membatalkan baris.
              const checked = e.target.checked;
              const isClaim = filters.status === "retention_claim" || String(filters.status).startsWith("claim_");
              setFilters({ ...filters, retention_only: checked, status: (checked && isClaim) ? "all" : filters.status });
            }} />
          Hanya Retensi (Invoice Sumber)
        </label>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" data-testid="filter-no-pic" checked={filters.no_pic}
            onChange={(e) => setFilters({ ...filters, no_pic: e.target.checked })} />
          Belum Ada PIC
        </label>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" data-testid="filter-no-followup" checked={filters.no_followup}
            onChange={(e) => setFilters({ ...filters, no_followup: e.target.checked })} />
          Belum Di-follow-up
        </label>
        <div className="ml-auto text-[10px] text-slate-500 tabular-nums" data-testid="collection-count">
          {rows.length} invoice
        </div>
      </div>

      {/* 2026-09-26: bulk assign bar — muncul saat ada baris dipilih */}
      {canAssign && Object.keys(selected).length > 0 && (
        <div className="flex items-center gap-2 bg-sky-50 border border-sky-300 px-3 py-2" data-testid="collection-bulk-bar">
          <span className="text-xs font-semibold text-sky-800 tabular-nums">{Object.keys(selected).length} invoice dipilih</span>
          <button type="button" data-testid="btn-bulk-assign" onClick={() => setBulkDlg(true)}
            className="text-[10px] uppercase tracking-wider bg-[#0B1D3A] hover:bg-[#12294f] text-white px-3 py-1.5 font-semibold">
            Assign PIC ke {Object.keys(selected).length} Invoice
          </button>
          <button type="button" data-testid="btn-bulk-clear" onClick={() => setSelected({})}
            className="text-[10px] uppercase tracking-wider border border-slate-300 px-2 py-1.5 hover:bg-slate-50">Batal Pilih</button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="py-12 text-center text-slate-500 text-sm" data-testid="collection-loading">Memuat…</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-slate-500 text-sm bg-white border border-dashed border-slate-300" data-testid="collection-empty">
          <FileText size={32} className="mx-auto text-slate-300 mb-2" />
          Tidak ada invoice untuk filter ini.
        </div>
      ) : isClaimView ? (
        /* 2026-10-19 closeout R221 (user spec poin 3): tabel KHUSUS klaim
           retensi — 14 kolom terpisah, bruto/PPh/neto TIDAK digabung jadi
           satu angka ambigu. Invoice sumber TIDAK dicampur sebagai baris. */
        <SyncStickyTableHead offset="context" testid="collection-table-claims" className="bg-white border border-purple-200">
          <table className="w-full text-sm">
            <thead className="bg-purple-50 border-b border-purple-200 text-[10px] uppercase tracking-wider text-purple-900">
              <tr>
                <th className="px-3 py-2 text-left">Proyek / WO</th>
                <th className="px-3 py-2 text-left">Klien</th>
                <th className="px-3 py-2 text-left">Invoice Sumber / Status Tautan</th>
                <th className="px-3 py-2 text-left">Nomor Klaim</th>
                <th className="px-3 py-2 text-left">Tanggal Klaim</th>
                <th className="px-3 py-2 text-left">Jatuh Tempo</th>
                <th className="px-3 py-2 text-right">Nilai Bruto</th>
                <th className="px-3 py-2 text-right">PPh / Potongan</th>
                <th className="px-3 py-2 text-right">Nilai Neto</th>
                <th className="px-3 py-2 text-right">Sudah Diterima</th>
                <th className="px-3 py-2 text-right">Sisa Klaim</th>
                <th className="px-3 py-2 text-center">Status Pembayaran</th>
                <th className="px-3 py-2 text-center">Status Verifikasi</th>
                <th className="px-3 py-2 text-left">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.entry_key} data-testid={`claim-row-${r.entry_key}`}
                  className="border-b border-slate-100 hover:bg-purple-50/40">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-800 text-xs">{r.project_name}</div>
                    {(r.variation_order_name || r.wo_number) && (
                      <div className="text-[10px] text-slate-500">{r.variation_order_name ? `VO ${r.variation_order_name}` : ""}{r.wo_number ? ` WO ${r.wo_number}` : ""}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">{r.client}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.retention_source_entry_id ? (
                      <span className="font-mono text-[10px] text-emerald-700" data-testid={`claim-src-${r.entry_id}`} title="ID invoice sumber retensi">
                        {r.retention_source_entry_id.slice(0, 8)}…
                      </span>
                    ) : (
                      <button type="button" data-testid={`btn-link-source-${r.entry_id}`}
                        onClick={() => setLinkSourceClaim(r)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-300 hover:bg-rose-100">
                        Belum Tertaut · Tautkan
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-700">{r.invoice_number || <span className="font-sans italic text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{r.invoice_date ? fmtDate(r.invoice_date) : <span className="text-slate-400">—</span>}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.due_date ? (
                      <span className={r.days_overdue > 0 && r.outstanding > 0 ? "text-rose-700 font-semibold" : "text-slate-700"}>
                        {fmtDate(r.due_date)}{r.days_overdue > 0 && r.outstanding > 0 ? ` (+${r.days_overdue}h)` : ""}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700" data-testid={`claim-bruto-${r.entry_id}`}>
                    {/* R223 closeout: bruto hanya ditampilkan bila ada di data
                        sumber; bila sumber hanya menyimpan neto → "—", jangan
                        menyalin neto menjadi bruto. R224 (2026-10-20): tampilan
                        "—" selalu didampingi badge "Nominal Perlu Verifikasi"
                        agar sebab-nya jelas (lihat kolom Status Verifikasi). */}
                    {r.claim_gross_amount != null ? formatIDR(r.claim_gross_amount) : (
                      <span className="inline-flex flex-col items-end">
                        <span className="text-slate-400">—</span>
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-rose-700" data-testid={`claim-bruto-unknown-${r.entry_id}`}>Nominal Perlu Verifikasi</span>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-sky-800" data-testid={`claim-pph-${r.entry_id}`}>{r.pph_amount ? `− ${formatIDR(r.pph_amount)}` : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#0B1D3A]" data-testid={`claim-neto-${r.entry_id}`}>
                    {/* R225: neto "—" saat nominal klaim tidak dapat dipastikan
                        — jangan tampilkan Rp0 / nilai invoice sumber. */}
                    {r.claim_bruto_known === false ? <span className="text-slate-400">—</span> : formatIDR(r.cash_due ?? r.gross_invoice_amount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700" data-testid={`claim-received-${r.entry_id}`}>{formatIDR(r.paid_amount)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.outstanding > 0 ? "text-rose-700" : "text-slate-400"}`} data-testid={`claim-sisa-${r.entry_id}`}>
                    {r.claim_bruto_known === false ? <span className="text-slate-400">—</span> : formatIDR(r.outstanding)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-800" data-testid={`status-claim-pay-${r.entry_id}`}>
                      {r.retention_state === "dicairkan" ? "Lunas" : r.retention_state === "dibayar_sebagian" ? "Dibayar Sebagian" : "Belum Dibayar"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.claim_needs_verification ? (
                      <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-300" data-testid={`status-claim-verif-${r.entry_id}`}>Perlu Verifikasi</span>
                    ) : (
                      <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-300" data-testid={`status-claim-verif-${r.entry_id}`}>Valid</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button data-testid={`btn-detail-${r.entry_key}`} onClick={() => openDetail(r)}
                      className="text-[10px] uppercase tracking-wider border border-slate-300 px-2 py-1 hover:bg-slate-50">Detail</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SyncStickyTableHead>
      ) : (
        <SyncStickyTableHead offset="context" testid="collection-table" className="bg-white border border-slate-200">
          <table className="w-full text-sm">
            {/* R74 fix: `ens-sticky-thead` dihapus — tabel ini dibungkus
                SyncStickyTableHead (JS clone); ganda = header bertumpuk. */}
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                {canAssign && (
                  <th className="px-2 py-2 w-8">
                    <input type="checkbox" data-testid="select-all-rows" aria-label="Pilih semua"
                      checked={rows.length > 0 && rows.every((r) => selected[r.entry_key])}
                      onChange={(e) => {
                        if (e.target.checked) setSelected(Object.fromEntries(rows.map((r) => [r.entry_key, true])));
                        else setSelected({});
                      }} />
                  </th>
                )}
                <th className="px-3 py-2 text-left">Client / Project</th>
                <th className="px-3 py-2 text-left">Invoice</th>
                <th className="px-3 py-2 text-left">Jatuh Tempo</th>
                <th className="px-3 py-2 text-right">Nilai</th>
                <th className="px-3 py-2 text-right">Diterima</th>
                <th className="px-3 py-2 text-right">Outstanding</th>
                <th className="px-3 py-2 text-center">Aging</th>
                <th className="px-3 py-2 text-right">Retensi</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-left">PIC</th>
                <th className="px-3 py-2 text-left">Last Follow-up</th>
                <th className="px-3 py-2 text-left">Tindak Lanjut</th>
                <th className="px-3 py-2 text-left">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.entry_key} data-testid={`row-${r.entry_key}`}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${r.status === "overdue" ? "bg-rose-50/30" : ""} ${selected[r.entry_key] ? "bg-sky-50/60" : ""}`}>
                  {canAssign && (
                    <td className="px-2 py-2">
                      <input type="checkbox" data-testid={`select-${r.entry_key}`} aria-label={`Pilih ${r.invoice_number || r.entry_id}`}
                        checked={!!selected[r.entry_key]}
                        onChange={(e) => setSelected((prev) => {
                          const next = { ...prev };
                          if (e.target.checked) next[r.entry_key] = true; else delete next[r.entry_key];
                          return next;
                        })} />
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-800">{r.client}</div>
                    <div className="text-[10px] text-slate-500">{r.project_name}{r.variation_order_name ? ` · VO ${r.variation_order_name}` : ""}{r.wo_number ? ` · WO ${r.wo_number}` : ""}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <InvoiceIdentityCell identity={r.identity} onOpenDetail={() => openDetail(r)} />
                    {r.retention_needs_source_link && (
                      <button type="button"
                        onClick={() => setLinkSourceClaim(r)}
                        data-testid={`btn-link-source-${r.entry_id}`}
                        className="mt-1 inline-flex items-center gap-1 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-300 hover:bg-rose-100">
                        Perlu Verifikasi · Tautkan
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.due_date ? (
                      <>
                        <div className={r.days_overdue > 0 && !isPaidRow(r) ? "text-rose-700 font-semibold" : "text-slate-700"}>{fmtDate(r.due_date)}</div>
                        {r.days_overdue > 0 && !isPaidRow(r) && <div className="text-[10px] text-rose-600">+{r.days_overdue} hari</div>}
                        {r.due_state === "jatuh_tempo_hari_ini" && <div className="text-[10px] text-amber-700 font-semibold" data-testid={`due-today-${r.entry_id}`}>Jatuh tempo hari ini</div>}
                      </>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {formatIDR(r.gross_invoice_amount)}
                    {/* 2026-10-19 (user request): baris klaim menampilkan potongan
                        PPh + nilai net (kas harus diterima) di bawah bruto. */}
                    {r.status === "retention_claim" && (r.pph_amount > 0 || (r.cash_due ?? r.gross_invoice_amount) !== r.gross_invoice_amount) && (
                      <div className="text-[9px] text-slate-500 leading-tight" data-testid={`claim-net-${r.entry_id}`}>
                        PPh −{formatIDR(r.pph_amount || 0)} · Net {formatIDR(r.cash_due ?? r.gross_invoice_amount)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatIDR(r.paid_amount)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.outstanding > 0 ? "text-rose-700" : "text-slate-400"}`}>
                    {formatIDR(r.outstanding)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {/* 2026-10-18 (closeout #1): klaim retensi TIDAK memakai
                        label aging invoice reguler — status retensi sendiri;
                        aging hanya berlaku dalam kelompok retensi (due date
                        klaim), TIDAK masuk jumlah "Jatuh Tempo Belum Diisi". */}
                    {r.status === "retention_claim" ? (
                      <span data-testid={`aging-claim-${r.entry_id}`} className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-purple-50 text-purple-700 border-purple-300">
                        {r.retention_state === "dicairkan"
                          ? "Retensi Sudah Dicairkan"
                          : r.due_date
                            ? (r.days_overdue > 0 ? `Klaim Retensi · Lewat ${r.days_overdue} hari` : `Klaim Retensi · JT ${fmtDate(r.due_date)}`)
                            : (r.retention_state === "dibayar_sebagian" ? "Klaim Retensi · Dibayar Sebagian" : "Klaim Retensi · Menunggu Pembayaran")}
                      </span>
                    ) : r.status === "paid" || r.outstanding <= 0 ? (
                      <span data-testid={`aging-lunas`} className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-slate-50 text-slate-600 border-slate-300" title={r.status_explanation}>
                        Tidak ada sisa · {r.origin_status?.origin_label || "—"}
                      </span>
                    ) : (
                      <span data-testid={`aging-${r.aging_bucket}`} className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${AGING_COLOR[r.aging_bucket]}`}>
                        {meta.aging_buckets.find((b) => b.key === r.aging_bucket)?.label || r.aging_bucket}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-orange-700">{r.retention_amount ? formatIDR(r.retention_amount) : "—"}</td>
                  <td className="px-3 py-2 text-center">
                    {/* 16-STATUS-KONSISTEN-R1: tiga sumbu status dipisah —
                        pembayaran (badge utama), verifikasi penerimaan, dan
                        klasifikasi selisih overpayment. */}
                    <div className="flex flex-col items-center gap-0.5">
                      {r.status === "retention_claim" ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          {/* 2026-10-19 closeout: PISAH status pembayaran vs
                              status verifikasi (= kualitas data). Klaim lunas
                              TETAP boleh berbadge Perlu Verifikasi. */}
                          <span className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-800" data-testid={`status-claim-pay-${r.entry_id}`}>
                            {r.retention_state === "dicairkan" ? "Lunas" : r.retention_state === "dibayar_sebagian" ? "Dibayar Sebagian" : "Belum Dibayar"}
                          </span>
                          {r.claim_needs_verification && (
                            <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-300" data-testid={`status-claim-verif-${r.entry_id}`}>
                              Perlu Verifikasi
                            </span>
                          )}
                        </div>
                      ) : r.origin_status ? (
                        <>
                          <OriginStatusBadges r={r} />
                          {r.days_overdue > 0 && r.outstanding > 0 && <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold ${STATUS_COLOR.overdue}`}>Terlambat</span>}
                          {r.status === "incomplete" && <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold ${STATUS_COLOR.incomplete}`}>Data Belum Lengkap</span>}
                        </>
                      ) : (
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLOR[isPaidRow(r) ? "paid" : (r.days_overdue > 0 && r.outstanding > 0 ? "overdue" : (r.status === "incomplete" ? "incomplete" : "open"))]}`} data-testid={`status-payment-${r.entry_id}`}>
                          {isPaidRow(r) ? "Lunas" : (r.days_overdue > 0 && r.outstanding > 0 ? "Terlambat" : (r.status === "incomplete" ? "Data Tidak Lengkap" : "Terbuka"))}
                        </span>
                      )}
                      {r.status === "needs_verification" && (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-amber-50 text-amber-800 border-amber-300" data-testid={`status-verif-${r.entry_id}`}>
                          Perlu Verifikasi
                        </span>
                      )}
                      {(r.classification_status === "needs_classification" || r.classification_status === "classified") && (
                        <span data-testid={r.classification_status === "needs_classification" ? `status-classify-${r.entry_id}` : `status-classified-${r.entry_id}`}>
                          <OverReceivedBadge
                            projectId={r.project_id}
                            overReceived
                            excessAmount={r.excess_amount}
                            classification={r.over_classification}
                            unclassifiedLabel="Perlu Klasifikasi"
                            onClassified={onOverClassified}
                          />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">
                    {r.pic_collection_name ? (
                      <span>{r.pic_collection_name}</span>
                    ) : (
                      <span className="text-slate-400 italic">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">
                    {r.last_followup_at ? (
                      <>
                        <div>{fmtDate(r.last_followup_at)}</div>
                        <div className="text-[10px] text-slate-500">{r.last_followup_result || ""}</div>
                        {r.next_followup_date && <div className="text-[10px] text-sky-600">→ {fmtDate(r.next_followup_date)}</div>}
                      </>
                    ) : <span className="text-slate-400 italic">Belum ada</span>}
                  </td>
                  <td className="px-3 py-2">
                    {(() => { const fs = FU_STATUS[r.followup_status] || FU_STATUS.dalam_proses; return (
                      <span data-testid={`fu-status-${r.entry_key}`} className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${fs.cls}`}>{fs.label}</span>
                    ); })()}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button data-testid={`btn-detail-${r.entry_key}`} onClick={() => openDetail(r)}
                        className="text-[10px] uppercase tracking-wider border border-slate-300 px-2 py-1 hover:bg-slate-50">Detail</button>
                      {canCreate && (
                        <button data-testid={`btn-followup-${r.entry_key}`} onClick={() => setFollowupDlg({ open: true, row: r })}
                          className="text-[10px] uppercase tracking-wider bg-[#FF5A1F] hover:bg-[#e04d15] text-white px-2 py-1">
                          + Follow-up
                        </button>
                      )}
                      {canAssign && (
                        <button data-testid={`btn-assign-${r.entry_key}`} onClick={() => setAssignDlg({ open: true, row: r })}
                          className="text-[10px] uppercase tracking-wider border border-sky-300 text-sky-700 px-2 py-1 hover:bg-sky-50">
                          PIC
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SyncStickyTableHead>
      )}

      {/* Detail drawer */}
      </>)}
      {detailRow && (
        <DetailDrawer row={detailRow} history={history} canCompleteIdentity={canCompleteIdentity}
          onOpenComplete={() => setCompleteDlg({ open: true, row: detailRow })}
          onOverClassified={onOverClassified} onChanged={load}
          onClose={() => { setDetailRow(null); setHistory([]); }} />
      )}
      <CompleteIdentityDialog open={completeDlg.open} row={completeDlg.row}
        onClose={() => setCompleteDlg({ open: false, row: null })}
        onSaved={(nr) => {
          setRows((prev) => prev.map((x) => (x.entry_key === nr.entry_key ? nr : x)));
          if (detailRow && detailRow.entry_key === nr.entry_key) setDetailRow(nr);
        }} />

      {followupDlg.open && (
        <CollectionFollowupDialog
          open={followupDlg.open}
          row={followupDlg.row}
          meta={meta}
          onClose={() => setFollowupDlg({ open: false, row: null })}
          onSaved={() => { setFollowupDlg({ open: false, row: null }); load(); }}
        />
      )}
      {assignDlg.open && (
        <CollectionAssignDialog
          open={assignDlg.open}
          row={assignDlg.row}
          onClose={() => setAssignDlg({ open: false, row: null })}
          onSaved={() => { setAssignDlg({ open: false, row: null }); load(); }}
        />
      )}
      {bulkDlg && (
        <CollectionBulkAssignDialog
          items={rows.filter((r) => selected[r.entry_key]).map((r) => ({ project_id: r.project_id, entry_id: r.entry_id, label: `${r.client} · ${r.invoice_number || r.project_name}` }))}
          onClose={() => setBulkDlg(false)}
          onSaved={() => { setBulkDlg(false); setSelected({}); load(); }}
        />
      )}
      <BulkFillDueDateDialog
        open={bulkFillOpen}
        onClose={() => setBulkFillOpen(false)}
        onSaved={() => { load(); }}
        onOpenDetail={(r) => setDetailRow(r)}
      />
      {/* 2026-10-19 (user request anti double-counting): dialog FAT menautkan
          klaim retensi legacy ke invoice sumber retensinya. */}
      <LinkRetentionSourceDialog
        open={!!linkSourceClaim}
        claim={linkSourceClaim}
        onOpenChange={(v) => { if (!v) setLinkSourceClaim(null); }}
        onLinked={() => { setLinkSourceClaim(null); load(); }}
      />
    </div>
  );
}

function SumCard({ icon, label, value, sub, tone, onClick, testid, active, tooltip }) {
  const tones = {
    sky: "border-sky-300", rose: "border-rose-300", orange: "border-orange-300",
    emerald: "border-emerald-300", slate: "border-slate-300",
  };
  const interactive = typeof onClick === "function";
  return (
    <div
      className={`bg-white border-l-4 border ${tones[tone] || tones.slate} px-3 py-2 ${interactive ? "cursor-pointer hover:bg-slate-50 transition-colors" : ""} ${active ? "ring-2 ring-inset ring-[#FF5A1F]" : ""}`}
      data-testid={testid}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
      title={tooltip || (interactive ? "Klik untuk membuka daftar invoice pembentuk angka ini" : undefined)}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{icon}{label}</div>
      <div className="text-lg font-bold tabular-nums text-[#0B1D3A] leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function DetailDrawer({ row, history, onClose, onOverClassified, onChanged, canCompleteIdentity, onOpenComplete }) {
  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-end" data-testid="collection-detail-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#0B1D3A] text-white px-4 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold">{row.client}</h3>
            <div className="text-xs text-white/70">{row.project_name} · {row.invoice_number ? `Invoice ${row.invoice_number}` : "Belum ada nomor invoice"}</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          {/* 2026-10-20 (user request): blok identitas kanonis — nomor, referensi,
              sumber, verifikasi, kelengkapan + tombol "Lengkapi Data" (gated). */}
          <InvoiceIdentityPanel identity={row.identity}
            onComplete={canCompleteIdentity ? onOpenComplete : null} />
          <div className="grid grid-cols-2 gap-2 text-sm">
            {/* R225: baris klaim — kartu pertama = Neto Klaim (bukan "Nilai
                Invoice" generik) supaya detail identik dengan tabel klaim. */}
            {row.status === "retention_claim" ? (
              <Info label="Nilai Neto Klaim" value={row.claim_bruto_known === false ? "— (Nominal Perlu Verifikasi)" : formatIDR(row.cash_due ?? row.gross_invoice_amount)} />
            ) : (
              <Info label="Nilai Invoice" value={formatIDR(row.gross_invoice_amount)} />
            )}
            <Info label="Pembayaran Diterima" value={formatIDR(row.paid_amount)} />
            <Info label="Outstanding" value={formatIDR(row.outstanding)} highlight={row.outstanding > 0} />
            <Info label="Jatuh Tempo" value={row.due_date ? fmtDate(row.due_date) : "—"} sub={row.days_overdue > 0 && !isPaidRow(row) ? `Terlambat ${row.days_overdue} hari` : (isPaidRow(row) ? "Tidak ada sisa" : (row.due_state_label || ""))} />
            <Info label="Retensi" value={formatIDR(row.retention_amount)} />
            <Info label="PIC Collection" value={row.pic_collection_name || "—"} />
            <Info label="Update Terakhir dari Accurate" value={row.accurate_last_updated ? fmtDate(row.accurate_last_updated) : "—"} />
            {/* 16-STATUS-KONSISTEN-R1: tiga status dipisah eksplisit agar konsisten
                dengan tabel — verifikasi penerimaan ≠ klasifikasi selisih ≠ pembayaran. */}
        <Info label="Status Verifikasi Penerimaan" value={REVIEW_STATUS_LABEL[row.review_status] || row.review_status || "Terverifikasi"}
          sub={row.identity?.verification_status?.reason ? `Alasan: ${row.identity.verification_status.reason}` : ""} />
            <Info label="Status Klasifikasi Selisih" value={
              row.project_over_received ? (
                <OverReceivedBadge projectId={row.project_id} overReceived excessAmount={row.excess_amount}
                  classification={row.over_classification} onClassified={onOverClassified} />
              ) : "Tidak ada selisih"
            } />
            <Info label="Asal Data" value={row.origin_status?.origin_label || "—"} sub={row.status_explanation || ""} />
            <Info label="Status Pembayaran" value={row.status_label || (isPaidRow(row) ? "Lunas" : "Terbuka")}
              sub={[row.reconciliation_required && "Perlu Rekonsiliasi", row.verification_required && "Perlu Verifikasi",
                (row.receipt_classification_status === "needs_classification" || row.receipt_classification_status === "partially_classified") && "Perlu Klasifikasi",
                row.days_overdue > 0 && row.outstanding > 0 && `Terlambat ${row.days_overdue} hari`].filter(Boolean).join(" · ")} />
            <Info label="Penerimaan Resmi Teralokasi" value={formatIDR(row.origin_status?.official_received ?? 0)} />
            <Info label="Penerimaan Legacy" value={formatIDR(row.origin_status?.legacy_received ?? 0)} />
            <Info label="Penerimaan Belum Diklasifikasikan" value={formatIDR(row.origin_status?.unclassified_received ?? 0)} />
          </div>

          {/* 2026-10-19 (user request "KLAIM RETENSI MENUNGGU PEMBAYARAN"):
              rincian klaim (Bruto/PPh/Net/Diterima/Sisa) + pembayaran & alokasi
              dari engine A.R. — tanpa rumus frontend berbeda. */}
          {row.status === "retention_claim" && (
            <ClaimPaymentDetail row={row} onChanged={onChanged} />
          )}

          {row.promise_date && (
            <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm">
              <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Janji Pembayaran Aktif</div>
              <div>Tanggal: {fmtDate(row.promise_date)}</div>
              {row.promise_amount && <div>Nilai: {formatIDR(row.promise_amount)}</div>}
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Histori Follow-up ({history.length})</div>
            {history.length === 0 ? (
              <div className="text-center py-4 text-slate-500 text-xs italic bg-slate-50 border border-slate-200">Belum ada follow-up untuk invoice ini.</div>
            ) : (
              <div className="space-y-2" data-testid="followup-history">
                {history.map((h) => (
                  <div key={h.id} className="border-l-2 border-sky-300 pl-3 py-2 bg-slate-50">
                    <div className="flex items-center justify-between text-[10px] text-slate-500">
                      <span>{fmtDate(h.followup_date)} · {h.created_by_name}</span>
                      <span className="uppercase tracking-wider font-semibold">{h.media}</span>
                    </div>
                    <div className="text-sm text-slate-800 mt-1">
                      <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-white border border-slate-200 mr-1">{h.result}</span>
                      {h.contact_name && <span className="text-slate-600">· {h.contact_name}</span>}
                    </div>
                    {h.notes && <div className="text-xs text-slate-700 mt-1">{h.notes}</div>}
                    {h.promise_date && (
                      <div className="text-[10px] text-emerald-700 mt-1">
                        Janji: {fmtDate(h.promise_date)}{h.promise_amount ? ` · ${formatIDR(h.promise_amount)}` : ""}
                      </div>
                    )}
                    {h.next_followup_date && (
                      <div className="text-[10px] text-sky-700 mt-0.5">Next follow-up: {fmtDate(h.next_followup_date)}</div>
                    )}
                    {(h.edit_history || []).length > 0 && (
                      <details className="mt-1 text-[10px] text-slate-500">
                        <summary className="cursor-pointer">{h.edit_history.length} revisi</summary>
                        {h.edit_history.map((eh) => (
                          <div key={`${eh.at}-${eh.by_name}`} className="mt-1 pl-2 border-l border-slate-200">
                            <div className="text-slate-600">{eh.by_name} · {fmtDate(eh.at)} — {eh.reason}</div>
                          </div>
                        ))}
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, sub, highlight }) {
  return (
    <div className="border border-slate-200 p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm tabular-nums ${highlight ? "text-rose-700 font-bold" : "text-slate-800 font-semibold"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
